/**
 * /founder-story — "Behind SociBaba"
 *
 * Premium Stripe / Notion-inspired founder page:
 *   • Warm off-white palette, strong serif display headline, quiet gutters
 *   • Founder photo + credentials card in the hero
 *   • Long-form "My Story" narrative (first-person, authentic — not dramatic)
 *   • Four principle cards (See beyond ratings · Multiple signals · Explain
 *     every score · Trust through transparency)
 *   • Vision pull-quote + a personal signed quote from the founder
 *   • Dual CTA (Explore SibaScore™ / See how it works)
 *   • Concept feedback form — writes to a dedicated `concept_feedback`
 *     Mongo collection (separate from the SibaScore feedback surface) so
 *     the founder can analyse product-concept validation cleanly.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Linkedin,
  MapPin,
  Eye,
  Layers,
  Lightbulb,
  ShieldCheck,
  Sparkles,
  Loader2,
  CheckCircle2,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  Heart,
  ExternalLink,
} from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { Footer } from "@/components/legal/Footer";
import { SEO } from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

// ── Founder identity ───────────────────────────────────────────────
export const FOUNDER = {
  firstName: "Yesuraj",
  fullName: "Yesuraj Gnanapragasam",
  title: "Founder, SociBaba",
  credentials: "Software Engineer · E-commerce Entrepreneur",
  location: "Bengaluru, India",
  photoUrl:
    "https://customer-assets-v7afamib.emergentagent.net/job_c7749e1a-94e1-4951-8364-d23f362b787f/artifacts/m8bzvtf2_Yesuraj%20Linyi%20Photo.png",
  linkedinUrl: "https://www.linkedin.com/in/yesuraj/",
} as const;

/** Other ventures the founder runs — real-world e-commerce operations
 *  that seeded the insights behind SibaScore. Rendered as small link
 *  chips under the founder identity card AND inline in "My Story". */
const VENTURES = [
  { label: "IndianSlingshot", url: "https://www.indianslingshot.com/", started: "2022" },
  { label: "IndoArchery",     url: "https://www.indoarchery.com/",     started: null },
] as const;

// ── The four principles that shaped SibaScore ─────────────────────
const PRINCIPLES = [
  {
    icon: Eye,
    title: "See beyond ratings",
    body: "A single star score compresses too much reality into too little signal. Real buying decisions deserve more nuance.",
  },
  {
    icon: Layers,
    title: "Multiple signals",
    body: "Reviews, videos, marketplace data, creator content, sentiment, complaint history — eleven signals, weighted honestly.",
  },
  {
    icon: Lightbulb,
    title: "Explain every score",
    body: "No black boxes. Every SibaScore number can be traced back to the signals and weights that produced it.",
  },
  {
    icon: ShieldCheck,
    title: "Trust through transparency",
    body: "Open weights. Public feedback. No paid reviews, no hidden agenda. The number you see is the number the model produced.",
  },
] as const;

const easeOut: [number, number, number, number] = [0.22, 1, 0.36, 1];

export default function FounderStoryPage() {
  const reduce = useReducedMotion();

  return (
    <div className="min-h-screen bg-[#FBFAF7] text-[#0B0C0E] antialiased" data-testid="founder-story-root">
      <SEO
        title="Behind SociBaba — Yesuraj Gnanapragasam, Founder"
        description="From a software engineer and e-commerce seller to building a smarter way to evaluate products. The story behind SociBaba and SibaScore™."
        canonical="/founder-story"
      />

      {/* ── Sticky slim top-bar ─────────────────────────────────────── */}
      <div className="sticky top-0 z-30 border-b border-black/[0.06] bg-[#FBFAF7]/85 backdrop-blur-md">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-14 flex items-center justify-between">
          <Link
            to="/"
            data-testid="founder-back-home"
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-black/60 hover:text-[color:var(--sb-saffron,#F97316)] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
            Back to SociBaba
          </Link>
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-black/50">
            Founder · Story
          </div>
          <a
            href={FOUNDER.linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="founder-linkedin-top"
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-black/60 hover:text-[color:var(--sb-saffron,#F97316)] transition-colors"
          >
            LinkedIn <Linkedin className="h-3.5 w-3.5" strokeWidth={2.5} />
          </a>
        </div>
      </div>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* soft warm gradient wash */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(1200px 500px at 15% 0%, rgba(249,115,22,0.10) 0%, transparent 50%), radial-gradient(900px 500px at 90% 30%, rgba(217,70,239,0.06) 0%, transparent 55%)",
          }}
        />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <motion.div
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: easeOut }}
                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1 text-[11px] uppercase tracking-[0.2em] font-semibold text-black/70"
                data-testid="founder-hero-eyebrow"
              >
                <Sparkles className="h-3 w-3 text-[color:var(--sb-saffron,#F97316)]" strokeWidth={2.5} />
                Behind SociBaba
              </motion.div>
              <motion.h1
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.06, ease: easeOut }}
                className="mt-5 font-serif-display font-bold text-[46px] sm:text-[64px] lg:text-[72px] leading-[1.02] tracking-[-0.02em] text-black"
                data-testid="founder-hero-title"
              >
                A quiet obsession
                <br />
                with honest signals.
              </motion.h1>
              <motion.p
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.14, ease: easeOut }}
                className="mt-6 max-w-xl text-lg sm:text-xl text-black/60 leading-relaxed"
                data-testid="founder-hero-subtitle"
              >
                From a software engineer and e-commerce seller to building a smarter
                way to evaluate products. This is why SociBaba exists.
              </motion.p>
            </div>

            {/* Founder card */}
            <motion.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: easeOut }}
              className="lg:col-span-5"
            >
              <div className="relative rounded-3xl border border-black/10 bg-white shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)] overflow-hidden">
                <div className="aspect-[4/5] overflow-hidden bg-black/5">
                  <img
                    src={FOUNDER.photoUrl}
                    alt={FOUNDER.fullName}
                    decoding="async"
                    className="h-full w-full object-cover"
                    data-testid="founder-story-photo"
                  />
                </div>
                <div className="p-5 sm:p-6">
                  <div className="font-serif-display font-bold text-xl sm:text-2xl leading-tight text-black" data-testid="founder-name">
                    {FOUNDER.fullName}
                  </div>
                  <div className="mt-1 text-sm text-black/70" data-testid="founder-title">
                    {FOUNDER.title}
                  </div>
                  <div className="mt-0.5 text-xs text-black/50">
                    {FOUNDER.credentials}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-1.5 text-xs text-black/60">
                      <MapPin className="h-3.5 w-3.5" strokeWidth={2.5} />
                      {FOUNDER.location}
                    </span>
                    <a
                      href={FOUNDER.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="founder-linkedin-card"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-black/70 hover:text-[color:var(--sb-saffron,#F97316)] transition-colors"
                    >
                      LinkedIn <Linkedin className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </a>
                  </div>

                  {/* Other ventures — real-world e-commerce shops. */}
                  <div className="mt-4 pt-4 border-t border-black/[0.08]" data-testid="founder-ventures">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-black/40 mb-2">
                      Ventures
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {VENTURES.map((v) => (
                        <a
                          key={v.url}
                          href={v.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`founder-venture-${v.label.toLowerCase()}`}
                          className="group inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white hover:border-[color:var(--sb-saffron,#F97316)]/60 hover:bg-[color:var(--sb-saffron,#F97316)]/5 px-2.5 py-1 text-xs font-semibold text-black/75 hover:text-[color:var(--sb-saffron,#F97316)] transition-colors"
                        >
                          {v.label}
                          <ExternalLink className="h-3 w-3 opacity-60 group-hover:opacity-100" strokeWidth={2.5} />
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── My Story ───────────────────────────────────────────────── */}
      <Section eyebrow="My Story" testId="founder-my-story">
        <StoryHeading>Why I built SibaScore.</StoryHeading>
        <div className="mt-8 space-y-6 text-lg text-black/75 leading-[1.75] max-w-2xl">
          <p>
            In 2022, I started{" "}
            <a
              href="https://www.indianslingshot.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="founder-story-indianslingshot-link"
              className="font-medium text-black underline underline-offset-4 decoration-[color:var(--sb-saffron,#F97316)]/60 hover:decoration-[color:var(--sb-saffron,#F97316)] transition-colors"
            >
              IndianSlingshot
            </a>{" "}
            — a small e-commerce venture I ran alongside my day job. I spent countless hours
            building customer trust, answering questions, explaining products. I later launched{" "}
            <a
              href="https://www.indoarchery.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="founder-story-indoarchery-link"
              className="font-medium text-black underline underline-offset-4 decoration-[color:var(--sb-saffron,#F97316)]/60 hover:decoration-[color:var(--sb-saffron,#F97316)] transition-colors"
            >
              IndoArchery
            </a>{" "}
            in the same spirit — selling specialised products to buyers who cared about the
            details.
          </p>
          <p>
            One thing kept surprising me. The star ratings on the products I sold were rarely aligned
            across platforms. Some reviews were genuinely helpful. Others were hard to interpret. And
            it turned out different customers were using those numbers in completely different ways.
          </p>
          <p>
            Over time I realised something simple: <b className="text-black">a single rating rarely tells
            the full story</b>. The number is compressed. The context is missing. The signal is thin.
          </p>
          <p>
            That is why I built <b className="text-black">SibaScore™</b> — an explainable 0-to-1000
            score that combines multiple trust signals into one honest assessment. It does not
            replace human judgment. It helps buyers make more informed decisions, and it helps sellers
            understand how their product is actually being perceived across the internet.
          </p>
        </div>
      </Section>

      {/* ── Principles ──────────────────────────────────────────────── */}
      <Section eyebrow="Principles" testId="founder-principles" muted>
        <StoryHeading>Four ideas that shaped the model.</StoryHeading>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {PRINCIPLES.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.45, delay: i * 0.06, ease: easeOut }}
                className="group rounded-2xl border border-black/10 bg-white p-6 sm:p-7 hover:border-black/25 transition-colors"
                data-testid={`founder-principle-${i}`}
              >
                <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-[color:var(--sb-saffron,#F97316)]/10 text-[color:var(--sb-saffron,#F97316)]">
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <div className="mt-4 font-serif-display font-bold text-xl leading-tight text-black">
                  {p.title}
                </div>
                <div className="mt-2 text-[15px] text-black/65 leading-relaxed">
                  {p.body}
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* ── Vision pull-quote ──────────────────────────────────────── */}
      <Section eyebrow="Vision" testId="founder-vision">
        <blockquote
          className="font-serif-display font-bold text-[32px] sm:text-[44px] lg:text-[52px] leading-[1.1] tracking-[-0.01em] text-black max-w-4xl"
          data-testid="founder-vision-quote"
        >
          <span className="text-black/40">"</span>
          Buyers deserve context, transparency, and confidence before they buy.
          <span className="text-black/40">"</span>
        </blockquote>
        <p className="mt-6 max-w-xl text-black/60 text-base sm:text-lg leading-relaxed">
          That's the whole thesis of SociBaba. Everything we build — SibaScore, AI Cart, Sancta, Intent
          Radar — is one attempt at pulling that vision closer to reality, one honest signal at a time.
        </p>
      </Section>

      {/* ── Personal signed quote ──────────────────────────────────── */}
      <Section eyebrow="A personal note" testId="founder-signoff" muted>
        <div className="max-w-2xl">
          <div className="rounded-3xl border border-black/10 bg-white p-8 sm:p-10 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.15)]">
            <div className="text-[15px] sm:text-base text-black/75 leading-[1.8]">
              "I've spent years both selling online and building software for a living. Both roles
              taught me the same lesson from opposite ends — trust doesn't come from marketing. It
              comes from being explainable. If SociBaba helps even one person avoid a bad purchase,
              or one honest brand get the credit they've quietly earned, the model has done its job."
            </div>
            <div className="mt-6 flex items-center gap-3">
              <img
                src={FOUNDER.photoUrl}
                alt={FOUNDER.fullName}
                decoding="async"
                className="h-11 w-11 rounded-full object-cover border border-black/10"
              />
              <div>
                <div
                  className="text-lg text-black leading-tight"
                  style={{ fontFamily: '"Caveat", "Playfair Display", serif', fontWeight: 700 }}
                  data-testid="founder-handwritten-signature"
                >
                  — {FOUNDER.firstName}
                </div>
                <div className="text-xs text-black/50 mt-0.5">{FOUNDER.title}</div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ── CTAs ───────────────────────────────────────────────────── */}
      <section className="border-t border-black/10 bg-white">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-16 sm:py-20 text-center">
          <h2 className="font-serif-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-black">
            Ready to see the model?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-black/60 max-w-xl mx-auto">
            Try SibaScore™ on any product — it takes about ten seconds.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              data-testid="founder-cta-siba"
              className="bg-black text-white hover:bg-black/90"
            >
              <Link to="/shop">Explore SibaScore™ <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              data-testid="founder-cta-how"
              className="border-black/15 text-black hover:bg-black/[0.03]"
            >
              <Link to="/#how-it-works">Discover how it works</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ── Concept feedback (goes to concept_feedback collection) ─── */}
      <ConceptFeedback />

      <Footer />
    </div>
  );
}

// ── Small reusable section wrapper ─────────────────────────────────
const Section: React.FC<{
  eyebrow: string;
  testId: string;
  muted?: boolean;
  children: React.ReactNode;
}> = ({ eyebrow, testId, muted = false, children }) => (
  <section
    className={`${muted ? "bg-white" : "bg-[#FBFAF7]"} border-t border-black/[0.06]`}
    data-testid={testId}
  >
    <div className="mx-auto max-w-6xl px-5 sm:px-8 py-16 sm:py-24">
      <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[color:var(--sb-saffron,#F97316)]">
        {eyebrow}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  </section>
);

const StoryHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="font-serif-display font-bold text-[34px] sm:text-[44px] leading-[1.08] tracking-[-0.01em] text-black max-w-3xl">
    {children}
  </h2>
);

// ── Concept feedback form (dedicated `concept_feedback` collection) ─
type ConceptRating = "love" | "like" | "unsure" | "dislike";

const RATING_META: Record<ConceptRating, { label: string; icon: React.ReactNode; tone: string }> = {
  love:    { label: "Love it",    icon: <Heart className="h-3.5 w-3.5" strokeWidth={2.5} />,       tone: "bg-rose-50 text-rose-700 border-rose-200" },
  like:    { label: "Like it",    icon: <ThumbsUp className="h-3.5 w-3.5" strokeWidth={2.5} />,    tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  unsure:  { label: "Unsure",     icon: <HelpCircle className="h-3.5 w-3.5" strokeWidth={2.5} />,  tone: "bg-amber-50 text-amber-700 border-amber-200" },
  dislike: { label: "Not for me", icon: <ThumbsDown className="h-3.5 w-3.5" strokeWidth={2.5} />,  tone: "bg-slate-100 text-slate-700 border-slate-200" },
};

const ConceptFeedback: React.FC = () => {
  const [rating, setRating] = React.useState<ConceptRating>("like");
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [wouldUse, setWouldUse] = React.useState<"yes" | "maybe" | "no">("yes");
  const [whatWorks, setWhatWorks] = React.useState("");
  const [improvements, setImprovements] = React.useState("");
  const [comment, setComment] = React.useState("");
  const [submittedId, setSubmittedId] = React.useState<string | null>(null);

  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const submit = useMutation({
    mutationFn: async () =>
      (await axios.post(`${backend}/api/feedback/concept`, {
        rating,
        would_use: wouldUse,
        email: email.trim(),
        name: name.trim() || undefined,
        what_works: whatWorks.trim() || undefined,
        improvements: improvements.trim() || undefined,
        comment: comment.trim(),
        source_url: typeof window !== "undefined" ? window.location.href : undefined,
      })).data,
    onSuccess: (d: any) => {
      setSubmittedId(d?.feedback_id || "ok");
      toast.success("Thank you — your input goes straight into our roadmap.");
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail || "Could not submit — please try again."),
  });

  const canSubmit =
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) &&
    comment.trim().length >= 5 &&
    !submit.isPending;

  return (
    <section
      className="border-t border-black/[0.06] bg-[#FBFAF7]"
      data-testid="founder-concept-feedback-section"
    >
      <div className="mx-auto max-w-3xl px-5 sm:px-8 py-16 sm:py-24">
        <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[color:var(--sb-saffron,#F97316)]">
          Feedback on the concept
        </div>
        <h2 className="mt-4 font-serif-display font-bold text-[32px] sm:text-[40px] leading-[1.1] tracking-[-0.01em] text-black">
          What do you think about the idea itself?
        </h2>
        <p className="mt-4 text-black/60 text-base sm:text-lg leading-relaxed max-w-2xl">
          Your honest reaction shapes the roadmap. This form is separate from the SibaScore model
          feedback — it goes directly to me for concept validation.
        </p>

        {submittedId ? (
          <div
            className="mt-10 rounded-3xl border border-emerald-200 bg-emerald-50 p-8 sm:p-10 text-center"
            data-testid="concept-feedback-thanks"
          >
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center mb-4">
              <CheckCircle2 className="h-6 w-6" strokeWidth={2.5} />
            </div>
            <h3 className="font-serif-display font-bold text-2xl text-black">Thank you.</h3>
            <p className="mt-2 text-sm text-black/60 max-w-md mx-auto">
              I read every submission personally. If it changes what we build, I'll email you.
            </p>
            <div className="mt-4 text-[10px] uppercase tracking-widest text-black/40">
              Reference · <span className="font-mono">{submittedId}</span>
            </div>
          </div>
        ) : (
          <form
            className="mt-10 space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) submit.mutate();
            }}
            data-testid="concept-feedback-form"
          >
            {/* Rating chips */}
            <div>
              <Label className="text-xs font-semibold text-black">How does the SociBaba concept land for you?</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {(Object.keys(RATING_META) as ConceptRating[]).map((r) => {
                  const m = RATING_META[r];
                  const active = r === rating;
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRating(r)}
                      data-testid={`concept-rating-${r}`}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${
                        active
                          ? "bg-black text-white border-black"
                          : `${m.tone} hover:border-black/40`
                      }`}
                    >
                      {m.icon}
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Would you use? */}
            <div>
              <Label className="text-xs font-semibold text-black">Would you use SibaScore™ before buying a product?</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {(["yes", "maybe", "no"] as const).map((v) => {
                  const active = v === wouldUse;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setWouldUse(v)}
                      data-testid={`concept-would-use-${v}`}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors capitalize ${
                        active
                          ? "bg-black text-white border-black"
                          : "bg-white text-black/70 border-black/15 hover:border-black/40"
                      }`}
                    >
                      {v === "yes" ? "Yes, definitely" : v === "maybe" ? "Maybe — need to see more" : "Probably not"}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Name + email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="concept-name" className="text-xs font-semibold text-black">
                  Your name <span className="text-black/40 font-normal">(optional)</span>
                </Label>
                <Input
                  id="concept-name"
                  data-testid="concept-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="How should we address you?"
                  className="mt-1.5 bg-white"
                />
              </div>
              <div>
                <Label htmlFor="concept-email" className="text-xs font-semibold text-black">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="concept-email"
                  data-testid="concept-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1.5 bg-white"
                />
              </div>
            </div>

            {/* What works */}
            <div>
              <Label htmlFor="concept-works" className="text-xs font-semibold text-black">
                What resonates with you? <span className="text-black/40 font-normal">(optional)</span>
              </Label>
              <Textarea
                id="concept-works"
                data-testid="concept-works"
                rows={3}
                maxLength={2000}
                value={whatWorks}
                onChange={(e) => setWhatWorks(e.target.value)}
                placeholder="Which parts of the story or the concept feel right?"
                className="mt-1.5 bg-white resize-none"
              />
            </div>

            {/* Improvements */}
            <div>
              <Label htmlFor="concept-improve" className="text-xs font-semibold text-black">
                What should we improve? <span className="text-black/40 font-normal">(optional)</span>
              </Label>
              <Textarea
                id="concept-improve"
                data-testid="concept-improve"
                rows={3}
                maxLength={2000}
                value={improvements}
                onChange={(e) => setImprovements(e.target.value)}
                placeholder="Weaknesses, gaps, or things we haven't thought about."
                className="mt-1.5 bg-white resize-none"
              />
            </div>

            {/* Main comment */}
            <div>
              <Label htmlFor="concept-comment" className="text-xs font-semibold text-black">
                Anything else you'd like to share? <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="concept-comment"
                data-testid="concept-comment"
                required
                minLength={5}
                maxLength={4000}
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Be direct — I read every one of these."
                className="mt-1.5 bg-white resize-none"
              />
              <div className="mt-1 text-[10px] text-black/40 text-right tabular-nums">
                {comment.length}/4000
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <p className="text-[11px] text-black/50">
                I'll only reply if I have follow-up questions on your feedback.
              </p>
              <Button
                type="submit"
                data-testid="concept-submit"
                disabled={!canSubmit}
                className="bg-black text-white hover:bg-black/90"
              >
                {submit.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send feedback <ArrowRight className="h-4 w-4 ml-1.5" />
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
